import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useBets } from '@/hooks/useBets';
import { useMarkets } from '@/hooks/useMarkets';
import { useMe } from '@/hooks/useMe';
import { formatCents } from '@/utils/money';
import { SPORTS } from '@/components/layout/sports';
import { useBetSlip } from './betSlipContext';
import { groupByFixture, type EventGroup } from './fixtures';
import { EmptyState, Glyph, MarketSkeleton, MatchCard, SectionHeader } from './MarketCard';
import { IconAlert, IconCalendar } from './marketUi';
import { BetRow } from './BetRow';

export function BetsPage() {
  const { data: me } = useMe();
  const { data: markets, isLoading: mkLoading, isError: mkError, refetch: refetchMarkets } = useMarkets();
  const { data: betsData, isLoading: betsLoading } = useBets();

  /* The sport filter lives in the URL, not in local state, so the left-hand nav
     can drive it: a nav click navigates to /bets?sport=<id> and this page is the
     single reader. Local state here would make the nav a dead end — it cannot
     reach into another component's useState. */
  const [searchParams, setSearchParams] = useSearchParams();
  const sportFilter = searchParams.get('sport');
  const setSportFilter = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('sport', id);
          else next.delete('sport');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /* The slip's state lives in BetSlipProvider, above the router outlet: the
     desktop rail and the mobile sheet both render it and are SIBLINGS of this
     page. `pick` opens the sheet below `xl`; on desktop the rail just updates. */
  const { isPicked, pick } = useBetSlip();

  const playBalance = me?.balances.balanceCents ?? 0;

  /* The board is ONE CARD PER EVENT (its primary market), not one per market,
     so an event with five markets is one tile with "All 5 markets ›", not five
     tiles. fixtures.ts does the grouping. */
  const events = groupByFixture(markets);
  const eventMatchesSport = (e: EventGroup, catId: string, catName: string) => {
    const s = (e.sport ?? '').toLowerCase();
    return s === catId.toLowerCase() || s === catName.toLowerCase() || s.includes(catName.toLowerCase());
  };
  const countFor = (catId: string, catName: string) => events.filter((e) => eventMatchesSport(e, catId, catName)).length;
  const activeCat = SPORTS.find((c) => c.id === sportFilter);
  const shownEvents = activeCat ? events.filter((e) => eventMatchesSport(e, activeCat.id, activeCat.name)) : events;
  const recentBets = betsData?.items.slice(0, 5) ?? [];

  return (
    <div className="flex flex-col gap-7">
      {/* Home shows the money you can bet with and nothing else (stakeholder
          feedback 2026-10-18, item 6): Savings and the withdrawable figure
          live on the Save tab. Mobile only — desktop has the right rail. */}
      <div className="xl:hidden rounded-2xl border border-brand-teal/20 bg-gradient-to-br from-[#102f3d] to-[#0a1c2c] p-4 flex items-end justify-between gap-3">
        <div>
          <div className="text-xs font-medium text-brand-teal/90">Play Balance</div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-white">{formatCents(playBalance)}</div>
        </div>
        <Link to="/savings" className="text-xs font-semibold text-brand-green/90 hover:brightness-110 whitespace-nowrap">Savings & withdrawals ›</Link>
      </div>

      {/* Browse sports & racing — sport rail */}
      <div>
        <SectionHeader title="Browse sports & racing" meta={`${SPORTS.length} categories`} />
        <div className="flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1">
          {SPORTS.map((s) => {
            const on = sportFilter === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setSportFilter(on ? null : s.id)}
                className={`shrink-0 min-w-[84px] flex flex-col items-center gap-1.5 rounded-xl border px-3.5 py-2.5 transition ${on ? 'border-brand-teal bg-brand-teal/10 text-brand-teal' : 'border-white/[0.08] bg-brand-surface/80 text-white/70 hover:border-white/20'}`}
              >
                <span className="relative">
                  <Glyph icon={s.icon} size={20} />
                  {s.live && <span className="absolute -top-0.5 -right-1.5 w-1.5 h-1.5 rounded-full bg-red-400" />}
                </span>
                <span className="text-[11px] font-medium">{s.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Featured / filtered events */}
      <div>
        <SectionHeader
          title={activeCat ? activeCat.name : 'Featured matches'}
          action={activeCat ? <button onClick={() => setSportFilter(null)} className="text-xs text-brand-teal hover:brightness-110">Clear filter</button> : (markets ? `${events.length} events · ${markets.length} markets` : undefined)}
        />
        {mkLoading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => <MarketSkeleton key={i} />)}
          </div>
        ) : mkError ? (
          <EmptyState
            icon={IconAlert}
            title="Couldn't load markets"
            subtitle="Something went wrong reaching the odds feed. Give it another go."
            action={<button onClick={() => refetchMarkets()} className="mt-1 rounded-lg border border-white/15 px-3 py-1.5 text-sm font-semibold text-brand-teal hover:bg-white/10 transition">Retry</button>}
          />
        ) : shownEvents.length === 0 ? (
          <EmptyState
            icon={IconCalendar}
            title={activeCat ? `No ${activeCat.name} markets right now` : 'No markets live right now'}
            subtitle={activeCat
              ? 'Try another sport, or check back soon — new markets open through the day.'
              : 'Prices refresh every few minutes. Browse a sport above or check back shortly.'}
            action={activeCat ? <button onClick={() => setSportFilter(null)} className="mt-1 rounded-lg border border-white/15 px-3 py-1.5 text-sm font-semibold text-brand-teal hover:bg-white/10 transition">Browse all sports</button> : undefined}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {shownEvents.map((e) => {
              const primary = e.markets[0];
              if (!primary) return null;
              return (
                <MatchCard
                  key={e.fixtureId}
                  market={primary}
                  marketCount={e.markets.length}
                  active={isPicked}
                  onPick={pick}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* All sports tiles */}
      <div>
        <SectionHeader title="All sports" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          {SPORTS.map((s) => {
            const n = countFor(s.id, s.name);
            return (
              <button
                key={s.id}
                onClick={() => setSportFilter(s.id)}
                className="relative flex flex-col gap-1.5 rounded-2xl border border-white/[0.08] bg-brand-surface/80 p-4 text-left hover:border-white/20 transition"
              >
                {s.live && <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-red-400" />}
                <span className="w-9 h-9 rounded-xl grid place-items-center bg-brand-teal/10 text-brand-teal"><Glyph icon={s.icon} size={20} /></span>
                <span className="font-semibold text-sm mt-1">{s.name}</span>
                <span className="text-xs text-white/40">{n} {n === 1 ? 'event' : 'events'}{s.live ? ' · live' : ''}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent bets — the last five; the full history, the streak meter and
          the win/loss record live on /bets/history. */}
      <div>
        <SectionHeader title="Recent bets" action={<Link to="/bets/history" className="text-xs text-brand-teal hover:brightness-110">My bets ›</Link>} />
        <div className="rounded-2xl border border-white/[0.08] bg-brand-surface/80 divide-y divide-white/[0.06]">
          {betsLoading && <p className="text-white/50 text-sm p-4">Loading…</p>}
          {!betsLoading && recentBets.length === 0 && <p className="text-white/45 text-sm p-4">No bets placed yet.</p>}
          {recentBets.map((bet) => <BetRow key={bet.id} bet={bet} markets={markets} compact />)}
        </div>
      </div>
    </div>
  );
}
